import styles from "./ServiceWizard.module.scss";
import { PageMode } from "../../../../common/types";
import { useServiceWizardPage } from "../serviceEventWizard/hooks/useServiceWizardPage";
import { ServiceWizardMainInputs } from "./components/ServiceWizardMainInputs";
import { RecurringEventSelector } from "../common/recurringEventSelector/RecurringEventSelector";
import { HiddenField } from "../../../../common/components/forms/inputs/hiddenField/HiddenField";
import { IServiceWizardFormModel } from "../serviceEventWizard/models/serviceWizardFormModel";
import { nameof } from "ts-simple-nameof";
import { DefaultButton } from "../../../../common/components/button/defaultButton/DefaultButton";
import { FormProvider } from "react-hook-form";
import { SingleSectionPageLayout } from "../../../../common/components/common/SingleSectionPageLayout";
import { IEventWizardInitialData } from "../models/eventWizardInitialDataModel";
import { EventType } from "../../../../common/enums";
import { useEffect, useMemo, useState } from "react";
import { IEmployeeWithAvailabilityModel } from "../../../../common/components/forms/inputs/instructorCalendar/models/employeeCalendarModels";
import { AppDialog } from "../../../../common/components/AppDialog/AppDialog";
import { UserWizardContents } from "../../../administration/usersAdministration/userWizard/components/UserWizardContents";
import { ISelectFieldModel } from "../../../../common/components/forms/inputs/dropdown/models/selectFieldModel";
import { mergeUserOptions } from "./utils/mergeUserOptions";

interface IServiceWizardProps {
  pageMode: PageMode;
  hasTabs: boolean;
  initialData?: IEventWizardInitialData;
  studioEmployees?: IEmployeeWithAvailabilityModel[];
  isLoading: boolean;
}

export const ServiceWizard = ({
  pageMode,
  hasTabs,
  initialData,
  studioEmployees,
  isLoading,
}: IServiceWizardProps) => {
  const {
    form,
    pageTitle,
    buttonTitle,
    dateText,
    enableMultisport,
    bufferAfterMinutes,
    onTrySave,
  } = useServiceWizardPage({
    pageMode,
    initialData,
    studioEmployees,
  });

  const [isRecurringActive, setIsRecurringActive] = useState(false);
  const [hasRecurringChecked, setHasRecurringChecked] = useState(false);
  const [recurringFormValid, setRecurringFormValid] = useState(true);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [showNewUserDialog, setShowNewUserDialog] = useState(false);
  const [createdUsers, setCreatedUsers] = useState<ISelectFieldModel[]>([]);

  // The client list refetches after a client is added; until it arrives, the
  // clients added here are shown from what the dialog sent.
  const userOptions = useMemo(
    () => mergeUserOptions(initialData?.allAvailableUsers, createdUsers),
    [initialData?.allAvailableUsers, createdUsers]
  );

  const onNewUserCreated = (newUserId: number, newUserName: string) => {
    setShowNewUserDialog(false);

    // The server can report success without an id (Identity rejected the user).
    if (!newUserId) {
      return;
    }

    setCreatedUsers((users) => [...users, { id: newUserId, name: newUserName }]);
    form.setValue("userId", newUserId, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleRecurringStatusChange = (
    isRecurring: boolean,
    hasChecked: boolean,
    isFormValid: boolean
  ) => {
    setIsRecurringActive(isRecurring);
    setHasRecurringChecked(hasChecked);
    setRecurringFormValid(isFormValid);
    setHasUserInteracted(true);
  };

  const isSubmitDisabled =
    !hasUserInteracted ||
    (isRecurringActive && !hasRecurringChecked) ||
    (isRecurringActive && !recurringFormValid);

  useEffect(() => {
    form.setValue("type", EventType.Service);
  }, [form]);

  // When there are no services to book (e.g. a restricted employee assigned to
  // none), only the info banner from ServiceWizardMainInputs is shown, so the
  // recurring toggle and the action buttons must be hidden too.
  const hasNoServices =
    pageMode !== "edit" &&
    !isLoading &&
    (initialData?.services?.length ?? 0) === 0;

  const titleComponent = (
    <>
      <div className={styles.root_title}>{pageTitle}</div>
      <div className={styles.root_date_text}>{dateText}</div>
    </>
  );

  const children = (
    <>
      <div className={styles.root_fields}>
        <ServiceWizardMainInputs
          initialData={initialData}
          userOptions={userOptions}
          enableMultisport={enableMultisport}
          studioEmployees={studioEmployees}
          bufferAfterMinutes={bufferAfterMinutes}
          pageMode={pageMode}
          isLoading={isLoading}
        />

        {!hasNoServices && (
          <RecurringEventSelector
            width={"100%"}
            pageMode={pageMode}
            isRecurringFieldName={nameof<IServiceWizardFormModel>(
              (x) => x.isRecurring
            )}
            recurringDaysFieldName={nameof<IServiceWizardFormModel>(
              (x) => x.recurringDays
            )}
            recurringPeriodFieldName={nameof<IServiceWizardFormModel>(
              (x) => x.recurringPeriod
            )}
            recurringPeriodTypeFieldName={nameof<IServiceWizardFormModel>(
              (x) => x.recurringPeriodType
            )}
            recurringIntervalFieldName={nameof<IServiceWizardFormModel>(
              (x) => x.recurringInterval
            )}
            recurringIntervalTypeFieldName={nameof<IServiceWizardFormModel>(
              (x) => x.recurringIntervalType
            )}
            checkboxLabel="Тази резерввация ще се повтаря ли?"
            onRecurringStatusChange={handleRecurringStatusChange}
            dateFieldName={nameof<IServiceWizardFormModel>((x) => x.date)}
            durationFieldName={nameof<IServiceWizardFormModel>(
              (x) => x.durationInMinutes
            )}
            bufferAfterMinutes={bufferAfterMinutes}
            employeeIdFieldName={nameof<IServiceWizardFormModel>(
              (x) => x.employeeId
            )}
            studioEmployees={studioEmployees}
          />
        )}

        <HiddenField name={nameof<IServiceWizardFormModel>((x) => x.date)} />
        <HiddenField
          name={nameof<IServiceWizardFormModel>((x) => x.studioId)}
        />
        <HiddenField name={nameof<IServiceWizardFormModel>((x) => x.type)} />
      </div>
      {!hasNoServices && (
        <div className={styles.root_buttons}>
          <DefaultButton
            label={buttonTitle}
            type="submit"
            tooltip={
              isSubmitDisabled ? "Първо проверете за конфликти" : undefined
            }
            disabled={isSubmitDisabled}
          />
          <DefaultButton
            label="Добави нов потребител"
            type="button"
            design="outline"
            onClick={() => setShowNewUserDialog(true)}
          />
        </div>
      )}
    </>
  );

  return (
    <>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onTrySave)}>
          <SingleSectionPageLayout
            title={titleComponent}
            children={children}
            height={"auto"}
            maxHeight={"80%"}
          />
        </form>
      </FormProvider>

      <AppDialog
        header="Добавяне на нов потребител"
        visible={showNewUserDialog}
        onHide={() => setShowNewUserDialog(false)}
      >
        <UserWizardContents
          pageMode="add"
          onCreated={onNewUserCreated}
          hideFontSize
        />
      </AppDialog>
    </>
  );
};
